import { FormHandle } from '@/hooks/use-form-handle.hook';
import { FC, useMemo } from 'react';
import InputControl from '@/components/form/input-control';
import { UiOption } from '@/lib/helpers';
import { Box, Stack } from '@mui/material';
import { AwsS3TelemetryModel } from '@/lib/models/telemetry/aws-s3-telemetry.model';
import { TelemetryModel } from '@/lib/models/telemetry/telemetry.model';
import { TelemetrySettings } from '@/lib/services/telemetry.service';
import TextInput from '@/components/form/text-input';
import Text from '@/components/text';
import { ConnectorModel } from '@/lib/models/connector/connector.model';
import { AwsConnectorModel } from '@/lib/models/connector/aws-connector.model';

interface Props {
  telemetry: AwsS3TelemetryModel;
  settings: TelemetrySettings;
  formHandle: FormHandle<TelemetryModel>;
  connectors: ConnectorModel[];
  isNew: boolean;
}

const AwsS3Telemetry: FC<Props> = ({ formHandle, settings, connectors, isNew }) => {
  const { values } = formHandle;

  const connectorOpts: UiOption[] = useMemo(
    () =>
      (connectors || [])
        .filter(({ type }) => type === 'aws')
        .map(({ id, name, status }) => ({
          label: name,
          value: id,
          disabled: status !== 'active',
        })),
    [connectors]
  );

  const showGovCloud = useMemo(() => {
    const connector = connectors.find(({ id }) => id === values.connectorId) as AwsConnectorModel;
    return connector?.config?.authType === 'access-keys';
  }, [connectors, values.connectorId]);

  const [userEventsUrl, auditEventsUrl] = useMemo(() => {
    const telemetry = new AwsS3TelemetryModel(values._props);
    return [
      telemetry.getDestinationUrl(settings, 'user-event'),
      telemetry.getDestinationUrl(settings, 'audit-event'),
    ];
  }, [values, settings]);

  return (
    <Box>
      <InputControl
        name="connectorId"
        label="Connector"
        formHandle={formHandle}
        type="select"
        options={connectorOpts}
        readonly={!isNew}
      />

      <InputControl
        name="config.bucketRegion"
        label="AWS Region"
        formHandle={formHandle}
        type="aws-region-select"
        withGovCloud={showGovCloud}
      />

      <InputControl
        name="config.bucketName"
        label="AWS S3 Bucket Name"
        formHandle={formHandle}
        required
      />

      <InputControl name="config.keyPrefix" label="AWS S3 Key Prefix" formHandle={formHandle} />

      <Stack gap={2} mt={1}>
        <Text bold size="small">
          AWS S3 Destination URLs
        </Text>

        <Box mb={0.5}>
          <TextInput
            name="userEventsDestinationUrl"
            label="User Events Destination URL"
            value={userEventsUrl}
            readonly
            size="small"
          />
        </Box>

        <TextInput
          name="auditEventsDestinationUrl"
          label="Audit Events Destination URL"
          value={auditEventsUrl}
          readonly
          size="small"
        />
      </Stack>
    </Box>
  );
};

export default AwsS3Telemetry;
